/**
 * RUNG r (surfaces.md §1.1): the method on **the variant she just missed**,
 * worked by her hand on the drawing, with `r` steps done for her. Every touch
 * goes into the rung's own trace, which rides on `explanation.rung_left` when
 * she leaves it. A rung never finishes the calculation and never writes a
 * number of its own (PRIEMONES.md, carried over).
 */
import { useEffect, useRef } from "react";

import type { ChildDrawing } from "./api";
import { drawingFor } from "../drawings/registry";
import { Recorder } from "./trace";
import { useChild } from "./ctx";

export interface RungHandle {
  leave(): void;
}

interface Props {
  drawing: ChildDrawing | null;
  rung: number;
  of: number;
  opened: string;
  item: string;
  trigger: "door" | "auto" | "reopen";
  picked?: string;
  /** Registers how to close this rung (rung_left) when she leaves it. */
  bind?: (h: RungHandle | null) => void;
}

/** The ladder's length for a variant: the drawing registry's rungs, at least 1
 *  (an explanation with beats only still earns one retry after the beats). */
export function ladderLength(drawing: ChildDrawing | null): number {
  const d = drawingFor(drawing?.id);
  return Math.max(1, d ? d.rungs(drawing?.params ?? {}) : 0);
}

export function Rung({ drawing, rung, of, opened, item, trigger, picked, bind }: Props) {
  const ctx = useChild();
  const def = drawingFor(drawing?.id);
  const rec = useRef<Recorder | null>(null);
  const eventId = useRef<string>("");
  const left = useRef(false);

  useEffect(() => {
    rec.current = new Recorder();
    left.current = false;
    eventId.current = ctx.emit("explanation.rung", { opened, item, rung, of, from: Math.max(0, rung - 1), trigger });
    const handle: RungHandle = {
      leave: () => {
        if (left.current || !rec.current) return;
        left.current = true;
        ctx.emit("explanation.rung_left", { rung_event: eventId.current, trace: rec.current.trace(), dwell_ms: rec.current.elapsed() });
      },
    };
    bind?.(handle);
    return () => {
      handle.leave();
      bind?.(null);
    };
    // One rung event per mount: a reopen remounts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, item, rung, trigger]);

  return (
    <div data-phase="RUNG" data-rung={rung}>
      {def ? (
        <def.Rung
          params={drawing?.params ?? {}}
          rung={rung}
          picked={picked}
          onTouch={(what) => rec.current?.push(["Mouse0", `btn:${what}`, "drawing", "button"])}
        />
      ) : null}
    </div>
  );
}
